// Мгновенное переключение экранов.
//
// Ощущение скорости у нативных оболочек берётся не из быстрого сервера, а из
// того, что экран рисуется СРАЗУ — из того, что уже знали, — и только потом
// тихо обновляется. Спиннер на каждом переходе делает интерфейс медленным даже
// при ответе в 80 мс, потому что человек видит пустоту.
//
// Здесь ровно это: `useResource` отдаёт последний известный ответ синхронно,
// а свежий запрос уходит в фоне и подменяет данные, когда придёт
// (stale-while-revalidate). Плюс `prefetch` — прогрев соседних вкладок, чтобы
// первый переход тоже был мгновенным.
//
// Кеш живёт в памяти вкладки: перезагрузка страницы — это осознанный «начни
// заново», а хранить дашборды в localStorage значит однажды показать вчерашние
// деньги как сегодняшние.
import { useEffect, useState } from 'preact/hooks';

type Entry = { data: unknown; at: number; inflight?: Promise<unknown> };

const store = new Map<string, Entry>();
const subs = new Map<string, Set<() => void>>();

/** Насколько ответ считается свежим. Полминуты: дашборд, который человек
 *  открывает по десять раз за смену, не должен долбить сеть, но и врать про
 *  очередь дольше этого не должен. */
const FRESH_MS = 30_000;

const notify = (key: string) => subs.get(key)?.forEach((fn) => fn());

async function revalidate<T>(key: string, loader: () => Promise<T>): Promise<void> {
  const prev = store.get(key);
  // Один запрос на ключ: три экрана, открытых подряд, не должны слать три
  // одинаковых GET.
  if (prev?.inflight) return;
  const p = loader()
    .then((data) => {
      store.set(key, { data, at: Date.now() });
      notify(key);
      return data;
    })
    .catch((e) => {
      // Ошибку не кешируем: следующий заход должен попробовать снова, а старые
      // данные лучше пустого экрана — они хотя бы были правдой минуту назад.
      store.set(key, { data: prev?.data, at: prev?.at ?? 0 });
      notify(key);
      throw e;
    });
  store.set(key, { data: prev?.data, at: prev?.at ?? 0, inflight: p });
  try {
    await p;
  } catch {
    /* проглочено выше — состояние уже обновлено */
  }
}

/**
 * Данные по ключу: сразу из кеша, обновление в фоне.
 *
 * `data` равен null только когда этот экран не открывали ни разу — тогда и
 * только тогда уместен индикатор загрузки.
 */
export function useResource<T>(key: string, loader: () => Promise<T>) {
  const [, force] = useState(0);

  useEffect(() => {
    const set = subs.get(key) ?? new Set();
    const fn = () => force((n) => n + 1);
    set.add(fn);
    subs.set(key, set);

    const entry = store.get(key);
    const stale = !entry || Date.now() - entry.at > FRESH_MS;
    if (stale) void revalidate(key, loader);

    return () => {
      set.delete(fn);
      if (set.size === 0) subs.delete(key);
    };
  }, [key]);

  const entry = store.get(key);
  return {
    data: (entry?.data as T | undefined) ?? null,
    /** true только при ПЕРВОЙ загрузке — фоновое обновление интерфейс не трогает. */
    loading: !entry?.data && !!entry?.inflight,
    /** Принудительно перечитать: после действия, которое изменило данные. */
    refresh: () => revalidate(key, loader),
  };
}

/** Выкинуть ключи из кеша — после записи, которая делает их неправдой. */
export function invalidate(prefix: string): void {
  for (const key of [...store.keys()]) {
    if (key.startsWith(prefix)) {
      store.delete(key);
      notify(key);
    }
  }
}

/** Прогреть ключ заранее: соседняя вкладка откроется без ожидания. */
export function prefetch<T>(key: string, loader: () => Promise<T>): void {
  const entry = store.get(key);
  if (entry && Date.now() - entry.at <= FRESH_MS) return;
  void revalidate(key, loader);
}
